<template>
  <label
    class="flex gap-3 py-1.5 transition"
    :class="[isDisabled && `opacity-50`]"
  >
    <input
      :id="fieldContext?.inputId"
      ref="input"
      v-model="modelValue"
      type="checkbox"
      :required="required ?? fieldContext?.required.value"
      :disabled="isDisabled"
      :name="name"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :indeterminate="indeterminate"
      :aria-describedby="ariaDescribedBy"
      :class="[
        `my-0.5 size-4 rounded border-border bg-input text-blue-600 focus:ring-blue-500`,
        isDisabled && `cursor-not-allowed`,
      ]"
      @focus="onFocus"
      @change="onChange"
      @click="onClick"
      @blur="onBlur"
    />

    <span class="text-sm">
      <slot />
      <span v-if="required ?? fieldContext?.required.value" class="text-red-500"
        >*</span
      >
    </span>
  </label>
</template>

<script lang="ts" setup>
import { computed, onMounted, useTemplateRef } from "vue";

import { injectFieldContext } from "./context";

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<boolean>({ default: false });

const props = defineProps<{
  required?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  name?: string;
  autofocus?: boolean;
  onFocus?: (event: FocusEvent) => void;
  onChange?: (event: Event) => void;
  onClick?: (event: MouseEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

defineExpose({ input });
</script>
